import { useEffect } from 'react';

import { ActivityType, useActivityStore } from '@/entities/activity';
import type { DailyActivityItem } from '@/entities/activity';
import { useSessionStore } from '@/entities/session';
import { openLink } from '@/shared/lib/telegram';
import { RADIUS, Skeleton, Squircle } from '@/shared/ui';

import styles from './DailyActivity.module.scss';

/**
 * Копирайт строк — из макета (узел 438:48191), числа приходят с сервера.
 *
 * ПОРЯДОК — не из макета: «Прогнозы, реакции, комментарии, сообщения»
 * задал заказчик 08.09.2026. Строка Прогнозов живёт отдельно, до этого
 * списка, — у неё свой источник чисел (пул дня Серии, а не дневной
 * лимит активности), поэтому здесь только три остальные.
 *
 * В строке «Реакции» показывается только `REACTION_GIVEN`. Складывать её
 * с `REACTION_RECEIVED` нельзя: у типов разные лимиты и разные Очки,
 * сумма не соответствует ни одному правилу игры и игроку необъяснима.
 * К тому же полученная реакция — не действие игрока, а «Сегодня доступно»
 * это список того, что он может сделать.
 *
 * `REFERRAL` приходит в ответе, но не рисуется: строке нужен свой копирайт
 * и своё действие (кнопка «Поделиться»), а макета под неё нет — пункт
 * в backlog.md.
 */
const ROWS = [
  {
    key: 'reactions',
    type: ActivityType.REACTION_GIVEN,
    title: 'Реакции',
    text: 'Ставь реакции и становись на шаг ближе к призам',
  },
  {
    key: 'comments',
    type: ActivityType.CHANNEL_COMMENT,
    title: 'Комментарии',
    text: 'Комментируй посты и поднимайся в рейтинге',
  },
  {
    key: 'messages',
    type: ActivityType.CHAT_MESSAGE,
    title: 'Сообщения',
    text: 'Общайся в чате и получай очки',
  },
] as const;

/** «Сегодня доступно» — дневная активность (узел 438:48191). */
export function DailyActivity() {
  const daily = useActivityStore((state) => state.daily);
  const isLoading = useActivityStore((state) => state.isLoading);
  const error = useActivityStore((state) => state.error);
  const load = useActivityStore((state) => state.load);
  const channelUrl = useSessionStore((state) => state.user?.channelUrl ?? null);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading && !daily) {
    return <Skeleton count={1} height={280} />;
  }

  const byType = new Map<string, DailyActivityItem>(
    (daily?.items ?? []).map((item) => [item.type, item]),
  );
  // Ссылка на КАНАЛ, а не на чат: кнопка стоит вплотную к блоку Ачивок,
  // где первая же — «Подпишись на Telegram-канал», и вела не туда.
  // Адрес собирает сервер из `game_setting` и отдаёт в профиле —
  // тот же источник, что у кнопки подписки на карточке игрока.

  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.root}>
      <h2 className={styles.title}>Сегодня доступно</h2>

      {/* Отдельного класса под ошибку в макете нет, а стили этой задачей
          не трогаем — берём типографику описания строки. */}
      {error ? (
        <p className={styles.text} role="alert" data-testid="activity-error">
          {error}
        </p>
      ) : null}

      <ul className={styles.list}>
        <li className={styles.row} data-testid="activity-predictions">
          <div className={styles.info}>
            <p className={styles.itemTitle}>Прогнозы</p>
            <p className={styles.text}>
              Предугадывай исходы матчей и приближайся к победе
            </p>
          </div>
          {/* Дневного лимита Прогнозов нет — потолок задаёт пул дня Серии.
              Пустой пул («сегодня матчей нет») рисуется прочерком, а не
              «0/0»: это не провал игрока. */}
          <p className={styles.progress}>
            {daily && daily.predictions.available > 0
              ? `${String(daily.predictions.made)}/${String(daily.predictions.available)}`
              : '—'}
          </p>
        </li>

        {ROWS.map((row) => {
          const item = byType.get(row.type);
          return (
            <li
              key={row.key}
              className={styles.row}
              data-testid={`activity-${row.key}`}
            >
              <div className={styles.info}>
                <p className={styles.itemTitle}>{row.title}</p>
                <p className={styles.text}>{row.text}</p>
                {item ? (
                  <p className={styles.reward}>
                    <img
                      src="/images/icons/fire-fill-outline.svg"
                      alt=""
                      aria-hidden
                      className={styles.fireIcon}
                    />
                    {item.pointsPerAction}
                  </p>
                ) : null}
              </div>
              <p className={styles.progress}>
                {item
                  ? `${String(item.awardedToday)}/${String(item.dailyLimit)}`
                  : '—'}
              </p>
            </li>
          );
        })}
      </ul>

      <Squircle asChild cornerRadius={RADIUS.xl}>
        <button
          type="button"
          className={styles.cta}
          // Ссылка задаётся в админке (`telegram_channel_url` или имя
          // канала). Пока её нет, открывать нечего: Telegram просто
          // закрыл бы мини-апп.
          disabled={channelUrl === null}
          data-testid="activity-channel-cta"
          onClick={() => {
            if (channelUrl) openLink(channelUrl);
          }}
        >
          Перейти в канал
        </button>
      </Squircle>
    </Squircle>
  );
}
